import type {
  DetailField,
  DetailFieldsSection,
} from "@/api/types";

/**
 * Hardcoded fallback descriptors for the customisable cards on the
 * Company-detail page. These are used in three cases:
 *
 *   1. The backend `GET /projects/{slug}/detail-fields` fetch is in
 *      flight on first paint.
 *   2. The fetch errors (network blip, project-type misconfig).
 *   3. The backend ships sections we don't have a key match for —
 *      we surface its fields via the generic FieldRenderer, but if
 *      the backend doesn't return a section at all, the hardcoded
 *      version below is what the operator sees.
 *
 * The shape mirrors `DetailFieldsSection` exactly so the same hook
 * (`useFieldVisibility`) and customiser popover work for both
 * fallback and live backend data with no branching.
 *
 * Section keys match the backend's keys ("info", "funding",
 * "founder", "signals"). When you add a field below, also wire a
 * bespoke renderer in CompanyDetail if the field needs custom UX
 * (autocomplete, cascading dropdowns, confirm dialog on toggle…) —
 * otherwise the generic FieldRenderer picks it up automatically
 * via `field.type`.
 */
export type DetailSection = DetailFieldsSection;

const FUNDING_STAGE_OPTIONS = [
  "pre_seed",
  "seed",
  "series_a",
  "series_b",
  "series_c",
  "growth",
];

const FOUNDER_ORIGIN_OPTIONS = [
  "french",
  "european",
  "indian",
  "israeli",
  "east_asian",
  "latam",
  "african",
  "middle_eastern",
  "southeast_asian",
  "canadian",
  "australian",
  "us_immigrant",
  "us_native",
  "unknown",
];

const TAX_ID_TYPE_OPTIONS = [
  "ein",
  "siren",
  "siret",
  "company_number",
  "kvk",
  "other",
];

/** Helper — fills in the `section` key (which DetailField requires)
 *  so each call site only has to declare it once at the section level. */
function field(
  sectionKey: string,
  data: Omit<DetailField, "section">,
): DetailField {
  return { section: sectionKey, ...data };
}

export const SECTION_INFO: DetailFieldsSection = {
  key: "info",
  label: "Information",
  fields: [
    field("info", { key: "domain", label: "Domain", type: "link", default_visible: true, editable: true }),
    field("info", { key: "sector", label: "Sector", type: "text", default_visible: true, locked: true, editable: true }),
    field("info", { key: "headcount", label: "Headcount", type: "number", default_visible: true, locked: true, editable: true }),
    field("info", { key: "city", label: "City", type: "text", default_visible: true, editable: true }),
    field("info", { key: "state", label: "State", type: "text", default_visible: true, editable: true }),
    field("info", { key: "country", label: "Country", type: "text", default_visible: true, editable: true }),
    field("info", { key: "description", label: "Description", type: "textarea", default_visible: false, editable: true }),
    field("info", { key: "source", label: "Source", type: "text", default_visible: false, editable: false }),
    field("info", { key: "tax_id", label: "Tax ID", type: "text", default_visible: false, editable: true }),
    field("info", {
      key: "tax_id_type",
      label: "Tax ID Type",
      type: "select",
      default_visible: false,
      editable: true,
      options: TAX_ID_TYPE_OPTIONS,
    }),
    field("info", { key: "created_at", label: "Created", type: "date", default_visible: true, editable: false }),
    field("info", { key: "updated_at", label: "Updated", type: "date", default_visible: false, editable: false }),
  ],
};

export const SECTION_FUNDING: DetailFieldsSection = {
  key: "funding",
  label: "Funding & founder",
  fields: [
    field("funding", {
      key: "funding_stage",
      label: "Funding Stage",
      type: "select",
      default_visible: true,
      locked: true,
      editable: true,
      source: "metadata",
      metadata_key: "funding_stage",
      options: FUNDING_STAGE_OPTIONS,
    }),
    // These two keys must stay equal to the ones the backend catalog
    // ships for the funding section. useEffectiveDetailFields dedupes
    // the backend/fallback merge on `key` and NOT on `metadata_key`, so
    // a fallback key the catalog does not also carry is treated as a
    // field the backend forgot and gets APPENDED — rendering a second,
    // identically-labelled row bound to a metadata key nothing writes
    // any more. It reads as an empty cell with no error. CB-12 renamed
    // both of these; the old spellings were funding_amount /
    // funding_amount_usd and funding_date / funding_date.
    field("funding", {
      key: "last_funding_amount_usd",
      label: "Funding Amount",
      type: "currency",
      default_visible: true,
      editable: true,
      source: "metadata",
      metadata_key: "last_funding_amount_usd",
    }),
    field("funding", {
      key: "last_funding_date",
      label: "Last Funding Date",
      type: "date",
      default_visible: true,
      editable: true,
      source: "metadata",
      metadata_key: "last_funding_date",
    }),
    field("funding", {
      // founder_origin lives inside the Funding card on the V1
      // layout. Not locked — operators can hide/show it from
      // the customiser like any other Funding field. (Toggling
      // its value still triggers an automatic rescore via
      // SCORE_IMPACTING_FIELDS, but that's separate from the
      // visibility lock.)
      key: "founder_origin",
      label: "Founder Origin",
      type: "select",
      default_visible: true,
      editable: true,
      source: "metadata",
      metadata_key: "founder_origin",
      options: FOUNDER_ORIGIN_OPTIONS,
    }),
  ],
};

export const SECTION_FOUNDER: DetailFieldsSection = {
  key: "founder",
  label: "Founder",
  fields: [
    field("founder", {
      key: "founder_origin",
      label: "Founder Origin",
      type: "select",
      default_visible: true,
      locked: true,
      editable: true,
      source: "metadata",
      metadata_key: "founder_origin",
      options: FOUNDER_ORIGIN_OPTIONS,
    }),
  ],
};

export const SECTION_SIGNALS: DetailFieldsSection = {
  key: "signals",
  label: "Hiring signals",
  fields: [
    field("signals", {
      key: "has_finance_lead",
      label: "Has Finance Lead",
      type: "boolean",
      default_visible: true,
      locked: true,
      editable: true,
      source: "metadata",
      metadata_key: "has_finance_lead",
    }),
    field("signals", {
      key: "is_hiring_finance",
      label: "Hiring Finance",
      type: "boolean",
      default_visible: true,
      locked: true,
      editable: true,
      source: "metadata",
      metadata_key: "is_hiring_finance",
    }),
    field("signals", {
      key: "is_hiring_hr_admin",
      label: "Hiring HR/Admin",
      type: "boolean",
      default_visible: true,
      locked: true,
      editable: true,
      source: "metadata",
      metadata_key: "is_hiring_hr_admin",
    }),
  ],
};

/* --------------------------------------------------------------- */
/* Backward-compat aliases — the existing imports from this module
 * use the old long names. We keep them pointing at the new section
 * objects so consumers can migrate at their own pace.            */
/* --------------------------------------------------------------- */
export const SECTION_INFORMATION = SECTION_INFO;
export const SECTION_HIRING = SECTION_SIGNALS;
